import { projects } from "@folio-2026/content"
import Experience from "@folio-2026/experience"
import { EVENTS, fallOff } from "@folio-2026/shared"
import gsap from "gsap"
import {
  color,
  float,
  Fn,
  length,
  mix,
  pow,
  texture,
  time,
  uniform,
  uv,
  vec2,
  vec3,
  vec4,
} from "three/tsl"
import * as THREE from "three/webgpu"
import { MathUtils, Texture } from "three/webgpu"
import { FolderApi } from "tweakpane"

export default class Image {
  experience: Experience
  sizes: Experience["sizes"]
  pointer: Experience["pointer"]
  resources: {
    [key: string]: Texture
  }
  scene: THREE.Scene
  mesh?: THREE.Mesh
  debugFolder?: FolderApi
  element?: HTMLElement
  bounds?: DOMRect
  fbmMap: THREE.Texture
  material?: THREE.MeshBasicNodeMaterial
  imageTextureNode: THREE.TextureNode

  settings = {
    progress: uniform(0),
    frequency: uniform(1.2),
    amplitude: uniform(1.3),
    alphaOffset: uniform(0.02),
    alphaMargin: uniform(0.05),
    burnColor: uniform(color("#750f0f")),
    burnOffset: uniform(0.35),
    burnMargin: uniform(0.4),
    burnExp: uniform(5),
    fireColor: uniform(color("#fb9e29")),
    fireOffset: uniform(0.26),
    fireMargin: uniform(0.43),
    fireScale: uniform(3.06),
    fireExp: uniform(4.67),
    fireMixExp: uniform(5.76),
  }

  animatedScroll = 0
  mouse = new THREE.Vector2(0, 0)
  rotationTarget = new THREE.Vector2(0, 0)
  maxTilt = new THREE.Vector2(
    MathUtils.degToRad(0.0003),
    MathUtils.degToRad(0.0005),
  )
  private quickToRotationX?: gsap.QuickToFunc
  private quickToRotationY?: gsap.QuickToFunc

  constructor({
    scene,
    debugFolder,
    fbmMap,
    resources,
  }: {
    scene: THREE.Scene
    debugFolder?: FolderApi
    fbmMap: THREE.Texture
    resources: {
      [key: string]: Texture
    }
  }) {
    this.experience = Experience.getInstance()
    this.sizes = this.experience.sizes
    this.pointer = this.experience.pointer

    this.resources = resources
    this.scene = scene
    this.debugFolder = debugFolder
    this.fbmMap = fbmMap
    this.fbmMap.wrapS = THREE.RepeatWrapping
    this.fbmMap.wrapT = THREE.RepeatWrapping

    this.imageTextureNode = texture(this.resources[projects[0].screenshot])

    this.setEventListeners()

    this.setDebug()
  }

  setEventListeners() {
    this.experience.on(EVENTS.Loader.CurtainRemovable, () => {
      this.createBounds()
      this.createMesh()
    })

    this.experience.on(EVENTS.Project.Entered, (event: unknown) => {
      requestAnimationFrame(() => {
        this.projectEnterHandler(event)
      })
    })
    this.experience.on(
      EVENTS.Project.Exiting,
      this.projectExitHandler.bind(this),
    )
  }

  private getElement() {
    if (this.element) return this.element

    const element = document.querySelector(
      "[data-project-image]",
    ) as HTMLElement | null
    if (!element) return

    this.element = element
    return element
  }

  createBounds() {
    if (!this.getElement()) {
      console.warn("Project image element not found: [data-project-image]")
      return
    }

    const bounds = this.element!.getBoundingClientRect()

    if (bounds.width <= 0 || bounds.height <= 0) {
      console.warn(
        "Project image bounds are not valid (on mobile the image is hidden)",
      )
      return
    }

    this.bounds = bounds
  }

  createMesh() {
    if (!this.bounds) {
      console.warn(
        "Project image bounds are not valid (on mobile the image is hidden)",
      )
      return
    }

    if (this.mesh) return

    const geometry = new THREE.PlaneGeometry(
      this.bounds.width,
      this.bounds.height,
    )
    geometry.translate(this.bounds.width / -2, 0, 0)
    this.material = new THREE.MeshBasicNodeMaterial({
      transparent: true,
    })

    this.material.outputNode = Fn(() => {
      const progress = this.settings.progress.oneMinus()
      const alpha = float(1)
      const baseColor = texture(this.imageTextureNode, uv()).rgb
      const burnColor = vec3(0).addAssign(baseColor)

      // Normalize distance so it stays roughly within the [0..2] range even if
      // the plane is sized in screen pixels (not 2x2 like the original test).
      const centeredUv = uv().sub(vec2(0.5))
      const distance = length(centeredUv).mul(3.0)

      // Jagged edge (fbm noise)
      distance.addAssign(
        texture(
          this.fbmMap,
          uv().mul(this.settings.frequency).mul(0.1).add(time.mul(0.01)),
        ).r.mul(this.settings.amplitude),
      )

      // Dark-red burn band
      const burnFalloff = fallOff(
        distance.add(this.settings.burnOffset),
        float(2).add(this.settings.amplitude),
        float(0).sub(this.settings.amplitude),
        this.settings.burnMargin,
        progress,
      )
      burnColor.assign(
        mix(
          burnColor,
          this.settings.burnColor,
          burnFalloff.pow(this.settings.burnExp),
        ),
      )

      // Fire band
      const fireFalloff = fallOff(
        distance.add(this.settings.fireOffset),
        float(2).add(this.settings.amplitude),
        float(0).sub(this.settings.amplitude),
        this.settings.fireMargin,
        progress,
      )

      const fire = this.settings.fireColor
        .mul(pow(fireFalloff, this.settings.fireExp))
        .mul(this.settings.fireScale)

      burnColor.assign(
        mix(burnColor, fire, fireFalloff.pow(this.settings.fireMixExp)),
      )

      // Alpha (transparent area)
      const alphaFalloff = fallOff(
        distance.add(this.settings.alphaOffset),
        float(2).add(this.settings.amplitude),
        float(0).sub(this.settings.amplitude),
        this.settings.alphaMargin,
        progress,
      ).oneMinus()

      alpha.mulAssign(alphaFalloff)

      return vec4(burnColor, alpha)
    })()

    this.mesh = new THREE.Mesh(geometry, this.material)

    this.scene.add(this.mesh)

    // Initialize GSAP quickTo for smooth rotation updates
    this.quickToRotationX = gsap.quickTo(this.mesh.rotation, "x", {
      duration: 0.8,
      ease: "power2.out",
    })
    this.quickToRotationY = gsap.quickTo(this.mesh.rotation, "y", {
      duration: 0.8,
      ease: "power2.out",
    })
  }

  projectEnterHandler(event: unknown) {
    const { url } = event as { url: string }

    const project = projects.find((project) => project.url === url)

    if (!project) {
      console.warn(`Project not found: [${url}]`)
      return
    }

    const imageTexture = this.resources[project.screenshot]
    if (!imageTexture) {
      console.warn(`Image texture not found: [${project.screenshot}]`)
      return
    }

    imageTexture.colorSpace = THREE.SRGBColorSpace
    imageTexture.needsUpdate = true

    this.settings.burnColor.value = new THREE.Color(project.burnColor)
    this.settings.fireColor.value = new THREE.Color(project.fireColor)
    // Do NOT reassign the node: the node graph is compiled once.
    // Update the node's value so the binding changes at runtime.
    this.imageTextureNode.value = imageTexture

    // Enter the card
    gsap.killTweensOf(this.settings.progress)
    gsap.to(this.settings.progress, {
      value: 1,
      duration: 4,
      ease: "power3.out",
    })
  }

  projectExitHandler() {
    gsap.killTweensOf(this.settings.progress)
    gsap.to(this.settings.progress, {
      value: 0,
      duration: 2,
      ease: "expo.out",
    })
  }

  setPosition() {
    if (!this.bounds || !this.mesh) return

    // Center mesh on the DOM element bounds
    const x = this.bounds.left + this.bounds.width / 2 - this.sizes.width / 2
    const y =
      -this.bounds.top -
      this.bounds.height / 2 +
      this.animatedScroll +
      this.sizes.height / 2

    this.mesh.position.set(x, y, this.mesh.position.z)
  }

  updateRotation() {
    if (
      !this.mesh ||
      !this.bounds ||
      !this.quickToRotationX ||
      !this.quickToRotationY
    )
      return

    // Calculate mouse position relative to image center
    const imageCenterX = this.bounds.left + this.bounds.width / 2
    const imageCenterY = this.bounds.top + this.bounds.height / 2

    const relativeX = this.pointer.x - imageCenterX
    const relativeY = this.pointer.y - imageCenterY

    // Normalize to -1 to 1 range based on image dimensions
    const x = relativeX / (this.bounds.width / 2)
    const y = relativeY / (this.bounds.height / 2)

    this.mouse.set(x, y)

    // natural tilt: move up -> tilt up, move right -> tilt right
    this.rotationTarget.set(
      -this.mouse.y * this.maxTilt.x,
      this.mouse.x * this.maxTilt.y,
    )

    // Use quickTo for smooth, performant updates
    this.quickToRotationX(this.rotationTarget.x)
    this.quickToRotationY(this.rotationTarget.y)
  }

  update() {
    this.updateRotation()
  }

  resize() {
    this.createBounds()
    this.setPosition()
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    const folder = this.debugFolder.addFolder({
      title: "🖼️ Image",
      expanded: false,
    })

    const settings = {
      progress: this.settings.progress.value,
      frequency: this.settings.frequency.value,
      amplitude: this.settings.amplitude.value,
      alphaOffset: this.settings.alphaOffset.value,
      alphaMargin: this.settings.alphaMargin.value,
      burnColor: `#${new THREE.Color(this.settings.burnColor.value).getHexString()}`,
      burnOffset: this.settings.burnOffset.value,
      burnMargin: this.settings.burnMargin.value,
      burnExp: this.settings.burnExp.value,
      fireColor: `#${new THREE.Color(this.settings.fireColor.value).getHexString()}`,
      fireOffset: this.settings.fireOffset.value,
      fireMargin: this.settings.fireMargin.value,
      fireScale: this.settings.fireScale.value,
      fireExp: this.settings.fireExp.value,
      fireMixExp: this.settings.fireMixExp.value,
    }

    folder
      .addBinding(settings, "progress", { min: 0, max: 1, step: 0.001 })
      .on("change", (ev) => {
        this.settings.progress.value = ev.value
      })

    folder
      .addBinding(settings, "frequency", { min: 0, max: 10, step: 0.01 })
      .on("change", (ev) => {
        this.settings.frequency.value = ev.value
      })

    folder
      .addBinding(settings, "amplitude", { min: 0, max: 10, step: 0.01 })
      .on("change", (ev) => {
        this.settings.amplitude.value = ev.value
      })

    folder
      .addBinding(settings, "alphaOffset", { min: 0, max: 1, step: 0.01 })
      .on("change", (ev) => {
        this.settings.alphaOffset.value = ev.value
      })

    folder
      .addBinding(settings, "alphaMargin", { min: 0, max: 1, step: 0.01 })
      .on("change", (ev) => {
        this.settings.alphaMargin.value = ev.value
      })

    folder
      .addBinding(settings, "burnColor", { view: "color" })
      .on("change", (ev) => {
        this.settings.burnColor.value = new THREE.Color(ev.value)
      })

    folder
      .addBinding(settings, "burnOffset", { min: 0, max: 1, step: 0.01 })
      .on("change", (ev) => {
        this.settings.burnOffset.value = ev.value
      })

    folder
      .addBinding(settings, "burnMargin", { min: 0, max: 1, step: 0.01 })
      .on("change", (ev) => {
        this.settings.burnMargin.value = ev.value
      })

    folder
      .addBinding(settings, "burnExp", { min: 0, max: 64, step: 1 })
      .on("change", (ev) => {
        this.settings.burnExp.value = ev.value
      })

    folder
      .addBinding(settings, "fireColor", { view: "color" })
      .on("change", (ev) => {
        this.settings.fireColor.value = new THREE.Color(ev.value)
      })

    folder
      .addBinding(settings, "fireOffset", { min: 0, max: 1, step: 0.01 })
      .on("change", (ev) => {
        this.settings.fireOffset.value = ev.value
      })

    folder
      .addBinding(settings, "fireMargin", { min: 0, max: 1, step: 0.01 })
      .on("change", (ev) => {
        this.settings.fireMargin.value = ev.value
      })

    folder
      .addBinding(settings, "fireScale", { min: 0, max: 10, step: 0.01 })
      .on("change", (ev) => {
        this.settings.fireScale.value = ev.value
      })

    folder
      .addBinding(settings, "fireExp", { min: 0, max: 10, step: 0.01 })
      .on("change", (ev) => {
        this.settings.fireExp.value = ev.value
      })

    folder
      .addBinding(settings, "fireMixExp", { min: 0, max: 10, step: 0.01 })
      .on("change", (ev) => {
        this.settings.fireMixExp.value = ev.value
      })
  }
}
